import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RuleEngineService, RulesGridItem, RulesGridRequest, RulesGridResponse, RuleType } from '../../../services/rule-engine.service';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';

type SortDir = 'ASC' | 'DESC' | '';

@Component({
  selector: 'app-manage-rules',
  templateUrl: './manage-rules.component.html',
  styleUrl: './manage-rules.component.scss'
})
export class ManageRulesComponent extends AnyoComponent {
  constructor(private ruleSvc: RuleEngineService, auth: AuthService, private router: Router, private route: ActivatedRoute) {
    super(auth);
  }

  tableLoading = false;
  page = 1;
  pageSize = 10;
  totalRecords = 0;
  pageSizeOptions = [10, 20, 50, 100];
  data: RulesGridItem[] = [];
  // filter states (no reactive form)
  searchInput = '';
  typeFilters: RuleType[] = [];
  activeFilter: boolean  = true;
  prioritySort: SortDir = '';
  priorityAsc = false;
  typeOptions: { label: string; value: RuleType }[] = Object.values(RuleType).map(type => ({ label: type, value: type }));

  async ready() {
    if (!this.user) return;
    const restored = this.restoreQueryFromUrl();
    if (restored) {
      this.applyRequestToState(restored);
    }
    this.fetch();
  }

  buildRequest(exportFlag = false): RulesGridRequest {
    const req: RulesGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: {
        TYPE: [],
        ACTIVE: [String(this.activeFilter)],
      },
      globalSearch: [],
    };
    if (this.prioritySort) {
      req.sort = { PRIORITY: this.prioritySort };
    }
    if(this.typeFilters.length) req.filters!.TYPE = this.typeFilters;
    if (this.searchInput && this.searchInput.trim()) req.searchQuery = { NAME: this.searchInput.trim() };
    return req;
  }

  fetch(): void {
    this.tableLoading = true;
    const req = this.buildRequest(false);
    this.updateUrlWithQuery(req);
    this.ruleSvc.getRulesGrid(req).subscribe({
      next: (res: RulesGridResponse) => {
        this.data = res.data;
        this.totalRecords = res.totalRecords;
        this.pageSize = res.pageSize;
        this.tableLoading = false;
      },
      error: () => {
        this.tableLoading = false;
      }
    });
  }

  clearFilters(): void {
    this.searchInput = '';
    this.typeFilters = [];
    this.activeFilter = true;
    this.prioritySort = '';
    this.page = 1;
    this.fetch();
  }

  nzPageIndexChange(p: number): void { this.page = p; this.fetch(); }
  nzPageSizeChange(s: number): void { this.pageSize = s; this.fetch(); }

  // Apply/reset per-filter from dropdowns

  resetTypeFilter(): void {
    this.typeFilters = [];
    this.page = 1;
    this.fetch();
  }


  applyPrioritySort(): void {
    this.page = 1;
    this.fetch();
  }
  resetPrioritySort(): void {
    this.prioritySort = '';
    this.page = 1;
    this.fetch();
  }

  onTypeTempChange(values: RuleType[]): void {
    console.log('onTypeTempChange', values);
    this.typeFilters = values;
  }
  

  onPriorityToggle(val: boolean): void {
    this.priorityAsc = val;
    this.prioritySort = val ? 'ASC' : 'DESC';
    this.page = 1;
    this.fetch();
  }

  onActiveToggle(val: boolean): void {
    this.activeFilter = val;
    this.page = 1;
    this.fetch();
  }

  // URL state sync helpers
  private updateUrlWithQuery(req: RulesGridRequest): void {
    const encoded = encodeURIComponent(JSON.stringify(req));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gridQuery: encoded },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private restoreQueryFromUrl(): RulesGridRequest | null {
    const qp = this.route.snapshot.queryParamMap.get('gridQuery');
    if (!qp) return null;
    try {
      const parsed = JSON.parse(decodeURIComponent(qp));
      return parsed as RulesGridRequest;
    } catch {
      return null;
    }
  }

  private applyRequestToState(req: RulesGridRequest): void {
    if (typeof req.page === 'number') this.page = req.page;
    if (typeof req.pageSize === 'number') this.pageSize = req.pageSize;
    if (req.searchQuery && typeof req.searchQuery.NAME === 'string') this.searchInput = req.searchQuery.NAME;
    if (req.filters) {
      if (Array.isArray(req.filters.TYPE)) this.typeFilters = req.filters.TYPE as RuleType[];
      if (Array.isArray(req.filters.ACTIVE) && req.filters.ACTIVE.length)
        this.activeFilter = req.filters.ACTIVE[0] === 'true';
    }
    if (req.sort && req.sort.PRIORITY) {
      this.prioritySort = req.sort.PRIORITY as SortDir;
      this.priorityAsc = this.prioritySort === 'ASC';
    } else {
      this.prioritySort = '';
      this.priorityAsc = false;
    }
  }
}
